import { SlateEditor, SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import {
  linkifyRange,
  linkifyWordBeforeCursor,
} from '@src/components/sco/RichMarkdownInput/plugins/links/linkify'
import { Editor, Node, Path, Range, Transforms } from 'slate'

export function withLinks(editor: SlateEditor, options: any = {}) {
  if (options?.disabled) {
    return editor
  }

  const { isInline, insertBreak, insertData, insertSoftBreak, insertText, onKeyDown } = editor

  editor.isInline = (element: SlateNode) => {
    // Skip links with URLs that match the pattern "mention:*"
    if (
      element.type === EditorElementTypes.LINK &&
      ((element as any).link?.startsWith('mention:') || (element as any).url?.startsWith('mention:'))
    ) {
      return isInline(element)
    }
    return element.type === EditorElementTypes.LINK ? true : isInline(element)
  }

  const linkAboveCursor = () =>
    Editor.above(editor, {
      match: n => !Editor.isEditor(n) && (n as SlateNode).type === EditorElementTypes.LINK,
    })

  // Leave the cursor after the link, not inside it, so following text (typed, or the next
  // pasted line, which splits the block) isn't pulled into the link or a copy of it
  const moveCursorPastLink = () => {
    const linkEntry = linkAboveCursor()
    if (linkEntry) {
      Transforms.select(editor, Editor.start(editor, Path.next(linkEntry[1])))
    }
  }

  editor.insertLink = (url: string, label?: string) => {
    if (!url) {
      return
    }

    // Skip links with URLs that match the pattern "mention:*"
    if (url.startsWith('mention:')) {
      return
    }

    const { selection } = editor
    const isCollapsed = selection && Range.isCollapsed(selection)
    const displayText = label || url
    const link = {
      type: EditorElementTypes.LINK,
      url,
      children: isCollapsed ? [{ text: displayText }] : [],
    }

    if (isCollapsed) {
      Transforms.insertNodes(editor, link)
      moveCursorPastLink()
    } else {
      Transforms.wrapNodes(editor, link, { split: true })
      Transforms.collapse(editor, { edge: 'end' })
    }
  }

  // Runs an insert, then linkifies everything it inserted
  const insertAndLinkify = (insert: () => void) => {
    const { selection } = editor
    if (!selection) {
      insert()
      return
    }

    const insertStart = Editor.pointRef(editor, Range.start(selection), { affinity: 'backward' })
    insert()
    const start = insertStart.unref()
    if (start && editor.selection) {
      linkifyRange(editor, { anchor: start, focus: Range.end(editor.selection) })
      moveCursorPastLink()
    }
  }

  // Paste goes in through the default handler (line by line), then URLs anywhere in what it
  // inserted become links and nothing else does
  editor.insertData = data => insertAndLinkify(() => insertData(data))

  // Typing: the word before the cursor is linkified when a space or line break goes in
  const linkifyBeforeBoundary = () => {
    if (!linkAboveCursor()) {
      linkifyWordBeforeCursor(editor)
      moveCursorPastLink()
    }
  }

  editor.insertText = text => {
    if (/^\s/.test(text)) {
      linkifyBeforeBoundary()
    }
    // More than one character at once (dictation, autocorrect, a pasted line) is linkified
    // like a paste
    if (text.length > 1) {
      insertAndLinkify(() => insertText(text))
    } else {
      insertText(text)
    }
  }

  editor.insertBreak = () => {
    linkifyBeforeBoundary()
    insertBreak()
  }

  editor.insertSoftBreak = () => {
    linkifyBeforeBoundary()
    insertSoftBreak()
  }

  // Add onKeyDown handler to terminate link element when a key is pressed
  editor.onKeyDown = (editor, event) => {
    // Check if we're at the end of a link element
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [linkNode] = Editor.nodes(editor, {
        // @ts-ignore
        match: n => !Editor.isEditor(n) && n.type === EditorElementTypes.LINK,
        at: selection,
      }) || [null]

      // If we're in a link and a key is pressed, handle it specially
      // Skip links with URLs that match the pattern "mention:*"
      if (linkNode && !(linkNode[0] as any).url?.startsWith('mention:')) {
        // Check if we're at the end of the link text
        const linkText = Node.string(linkNode[0])
        const linkPath = linkNode[1]
        const point = selection.anchor
        const [parentNode] = Editor.parent(editor, point.path)
        const offset = point.offset

        // If we're at the end of the link text and a printable character or space is pressed
        if (
          offset === linkText.length &&
          (event.key.length === 1 || event.key === ' ' || event.key === 'Enter' || event.key === 'Tab')
        ) {
          event.preventDefault()

          // Move the selection out of the link
          Transforms.select(editor, Editor.after(editor, linkPath))

          // Insert the character that was pressed
          if (event.key.length === 1) {
            Transforms.insertText(editor, event.key)
          } else if (event.key === ' ') {
            Transforms.insertText(editor, ' ')
          } else if (event.key === 'Enter') {
            Transforms.splitNodes(editor)
          } else if (event.key === 'Tab') {
            // Let the default tab behavior happen
            return
          }

          return // Don't call the original onKeyDown handler
        }
      }
    }

    // Call the original onKeyDown handler
    if (onKeyDown) {
      onKeyDown(editor, event)
    }
  }

  return editor
}
